// React Query wiring for the market research async flow.
// Two hooks: a polling status query, and a "run" mutation.
//
// `useMarketResearchStatus(projectId)` fetches the latest research
// state for the project and self-polls every 5s while status is
// `running`. On transition to `complete`, invalidates the canvas data
// query so the newly-written signal nodes + edges appear without a
// hard refresh.
//
// `useRunMarketResearch()` POSTs to the start endpoint; on success
// invalidates the status query so the hook flips into polling mode
// without waiting for the next interval tick.

import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { toApiError } from '@/lib/plan-limit';
import type {
  MarketResearchRunResponse,
  MarketResearchStatusResponse,
  MarketResearchStatus,
} from '@/types/api';

const POLL_INTERVAL_MS = 5000;

interface UseMarketResearchStatusOptions {
  enabled?: boolean;
}

export function useMarketResearchStatus(projectId: string, options: UseMarketResearchStatusOptions = {}) {
  const queryClient = useQueryClient();
  const enabled = (options.enabled ?? true) && !!projectId;
  const lastStatusRef = useRef<MarketResearchStatus | null>(null);

  const query = useQuery<MarketResearchStatusResponse | null>({
    queryKey: ['market-research', projectId],
    queryFn: async () => {
      const response = await api.getMarketResearchStatus(projectId);
      // Throw on non-success so React Query treats this as a retry-
      // able error and keeps the last-known `data` around. Returning
      // null would mark the fetch as successful with data=null, which
      // would make refetchInterval return false and stop polling
      // permanently even if the prior known state was `running`.
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch market-research status');
      }
      return response.data;
    },
    enabled,
    refetchInterval: (q) => {
      const data = q.state.data as MarketResearchStatusResponse | null | undefined;
      return data?.status === 'running' ? POLL_INTERVAL_MS : false;
    },
  });

  // Side-effect: when status transitions to complete, refresh the
  // canvas data + project query so the new signal nodes + edges land
  // in the canvas-store and the journey badge picks up the changes.
  useEffect(() => {
    const status = query.data?.status ?? null;
    const prev = lastStatusRef.current;
    if (prev === 'running' && status === 'complete') {
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    }
    lastStatusRef.current = status;
  }, [query.data?.status, projectId, queryClient]);

  return query;
}

export function useRunMarketResearch() {
  const queryClient = useQueryClient();

  return useMutation<MarketResearchRunResponse, Error, string>({
    mutationFn: async (projectId: string) => {
      const response = await api.runMarketResearch(projectId);
      if (!response.success || !response.data) {
        throw toApiError(response, 'Failed to start market research');
      }
      return response.data;
    },
    onSuccess: (_data, projectId) => {
      // Flip the status hook into polling mode immediately — the next
      // poll tick will already see status: 'running'.
      queryClient.invalidateQueries({ queryKey: ['market-research', projectId] });
      // A run consumed the project's research-run allowance.
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}
